/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import {
  Trash2,
  Plus,
  Clock,
  Users,
  ChevronDown,
  ChevronUp,
  Play,
  Pause,
  Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useMatchEvents,
  useLogEvent,
  useDeleteEvent,
} from "@/hooks/api/useEvents";
import { eventKeys } from "@/hooks/api/useEvents";
import { useMatch, matchKeys } from "@/hooks/api/useMatches";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import EventLogForm from "@/pages/live-scorer/EventLogForm";
import BulkAppearanceForm from "@/pages/live-scorer/BulkAppearanceForm";
import type { Match, MatchEvent, EventType } from "@/types/api.types";
import { useMatchTimer } from "@/hooks/useMatchTimer";
import { useUpdateMatchStatus, useUpdateMatch } from "@/hooks/api/useMatches";

interface LiveMatchCardProps {
  match: Match;
}

const EVENT_ICONS: Record<EventType, string> = {
  GOAL: "⚽",
  ASSIST: "🅰️",
  CLEAN_SHEET: "🧤",
  YELLOW_CARD: "🟨",
  RED_CARD: "🟥",
  PENALTY_SAVED: "🧱",
  PENALTY_MISSED: "❌",
  OWN_GOAL: "😬",
  APPEARANCE: "👟",
  LONG_APPEARANCE: "⏱️",
};

const EVENT_COLORS: Record<EventType, string> = {
  GOAL: "bg-green-100 text-green-800 border-green-200",
  ASSIST: "bg-blue-100 text-blue-800 border-blue-200",
  CLEAN_SHEET: "bg-teal-100 text-teal-800 border-teal-200",
  YELLOW_CARD: "bg-yellow-100 text-yellow-800 border-yellow-200",
  RED_CARD: "bg-red-100 text-red-800 border-red-200",
  PENALTY_SAVED: "bg-purple-100 text-purple-800 border-purple-200",
  PENALTY_MISSED: "bg-orange-100 text-orange-800 border-orange-200",
  OWN_GOAL: "bg-gray-100 text-gray-800 border-gray-200",
  APPEARANCE: "bg-indigo-100 text-indigo-800 border-indigo-200",
  LONG_APPEARANCE: "bg-violet-100 text-violet-800 border-violet-200",
};

const APPEARANCE_TYPES: EventType[] = ["APPEARANCE", "LONG_APPEARANCE"];

export default function LiveMatchCard({ match }: LiveMatchCardProps) {
  const [showForm, setShowForm] = useState(false);
  const [showBulkAppearance, setShowBulkAppearance] = useState(false);
  const [startersMode, setStartersMode] = useState(false);
  const [eventsExpanded, setEventsExpanded] = useState(true);
  const [appearancesExpanded, setAppearancesExpanded] = useState(false);
  const [editingScore, setEditingScore] = useState(false);
  const [scoreInput, setScoreInput] = useState({ home: 0, away: 0 });
  const timer = useMatchTimer(match.id);
  const queryClient = useQueryClient();

  // Initialize timer when match is LIVE
  useEffect(() => {
    if (match.status === "LIVE" && timer.state === "idle") {
      // `liveStartTime` is the wall-clock time the match transitioned to LIVE.
      // The backend sets it on the go-live transition; older matches (from
      // before that column existed) may have it null — for those we fall
      // back to `kickoffTime` because the old backend used to overwrite
      // kickoffTime with the actual live moment.
      //
      // We deliberately never fall back to `updatedAt`: it gets bumped by
      // every event log / edit and produces "time since last write" rather
      // than "time since match started".
      const liveStartTimestamp = match.liveStartTime
        ? new Date(match.liveStartTime).getTime()
        : new Date(match.kickoffTime).getTime();

      const now = Date.now();
      const elapsedSeconds = Math.floor((now - liveStartTimestamp) / 1000);

      // Sanity bounds: must be non-negative AND under 2 hours. Anything
      // outside that is almost certainly bad data — start fresh at 0.
      const startFrom =
        elapsedSeconds >= 0 && elapsedSeconds < 7200 ? elapsedSeconds : 0;

      console.log(
        `⏱️ Starting timer for match ${match.id} from ${startFrom}s (${Math.floor(startFrom / 60)}m ${startFrom % 60}s)`,
      );
      timer.start(startFrom);
    }
  }, [match.id, match.status]); // Run when match ID or status changes

  // Fetch fresh score from DB — fixes score reset on refresh
  const { data: matchDetail } = useMatch(match.id);
  const liveScore = {
    home: matchDetail?.homeScore ?? match.homeScore,
    away: matchDetail?.awayScore ?? match.awayScore,
  };

  const { data: events, isLoading: eventsLoading } = useMatchEvents(match.id);
  const { mutate: logEvent, isPending: isLogging } = useLogEvent();
  const { mutate: deleteEvent, isPending: isDeleting } = useDeleteEvent();
  const { mutate: updateStatus, isPending: isCompleting } = useUpdateMatchStatus();
  const { mutate: updateMatch, isPending: isSavingScore } = useUpdateMatch();

  // Current match minute for auto-filling event form
  const currentMinute = Math.max(1, Math.floor(timer.seconds / 60) + 1);

  const handleOpenScoreEdit = () => {
    setScoreInput({ home: liveScore.home, away: liveScore.away });
    setEditingScore(true);
  };

  const handleSaveScore = () => {
    updateMatch(
      { id: match.id, data: { homeScore: scoreInput.home, awayScore: scoreInput.away } },
      {
        onSuccess: () => {
          toast.success("Score updated");
          setEditingScore(false);
          invalidateScore();
        },
        onError: (e: any) =>
          toast.error(e.response?.data?.message || "Failed to update score"),
      }
    );
  };

  // Split events into two groups, newest first
  const matchEvents = (events || [])
    .filter((e) => !APPEARANCE_TYPES.includes(e.eventType))
    .reverse();

  const appearanceEvents = (events || [])
    .filter((e) => APPEARANCE_TYPES.includes(e.eventType))
    .reverse();

  const hasAppearances = (events || []).some((e) =>
    APPEARANCE_TYPES.includes(e.eventType),
  );

  const invalidateScore = () => {
    queryClient.invalidateQueries({ queryKey: matchKeys.detail(match.id) });
    queryClient.invalidateQueries({ queryKey: matchKeys.live() });
  };

  const handleLogEvent = (data: {
    playerId: number;
    eventType: EventType;
    minute: number;
    assistPlayerId?: number;
  }) => {
    logEvent(
      { ...data, matchId: match.id },
      {
        onSuccess: (newEvent: any) => {
          toast.success(
            `${EVENT_ICONS[data.eventType]} ${data.eventType} logged for ${newEvent.player?.name} — +${newEvent.points} pts`,
          );
          // Refetch score from DB for goal and own goal
          if (data.eventType === "GOAL" || data.eventType === "OWN_GOAL") {
            invalidateScore();
          }
          setShowForm(false);
        },
        onError: (error: any) =>
          toast.error(error.response?.data?.message || "Failed to log event"),
      },
    );
  };

  const handleDeleteEvent = (event: MatchEvent) => {
    if (
      !confirm(`Delete this ${event.eventType} event? Points will be reversed.`)
    )
      return;

    deleteEvent(
      { id: event.id, matchId: match.id },
      {
        onSuccess: () => {
          toast.success("Event deleted and points reversed");
          // Always refetch score after deletion
          invalidateScore();
        },
        onError: (error: any) =>
          toast.error(
            error.response?.data?.message || "Failed to delete event",
          ),
      },
    );
  };

  const handleShowForm = () => {
    setShowBulkAppearance(false);
    setShowForm(!showForm);
  };

  const handleCompleteMatch = () => {
    const warnings: string[] = [];
    if (!hasAppearances) warnings.push("⚠️ No appearances have been logged yet");
    if (!timer.isFullTime) warnings.push("⚠️ Timer has not reached full time");

    const warningText = warnings.length > 0 ? `\n\n${warnings.join("\n")}` : "";

    if (
      !confirm(
        `Complete match ${match.homeNation.name} ${liveScore.home}–${liveScore.away} ${match.awayNation.name}?${warningText}\n\nThis cannot be undone.`,
      )
    )
      return;

    updateStatus(
      { id: match.id, status: "COMPLETED" },
      {
        onSuccess: () =>
          toast.success("Match completed! Points will be calculated via gameweek."),
        onError: (e: any) =>
          toast.error(e.response?.data?.message || "Failed to complete match"),
      },
    );
  };

  const handleShowBulk = (isStartersMode: boolean) => {
    setShowForm(false);
    setStartersMode(isStartersMode);
    // toggle off if already showing same mode
    if (showBulkAppearance && startersMode === isStartersMode) {
      setShowBulkAppearance(false);
    } else {
      setShowBulkAppearance(true);
    }
  };

  const { mutate: awardCleanSheet } = useMutation({
    mutationFn: (teamSide: "home" | "away") =>
      apiClient.post(API_ENDPOINTS.EVENTS.CLEAN_SHEET, {
        matchId: match.id,
        teamSide,
      }),
    onSuccess: (res: any) => {
      toast.success(res.message || "Clean sheet awarded!");
      queryClient.invalidateQueries({ queryKey: eventKeys.match(match.id) });
    },
    onError: (error: any) =>
      toast.error(
        error.response?.data?.message || "Failed to award clean sheet",
      ),
  });

  const handleCleanSheet = (teamSide: "home" | "away") => {
    const nation =
      teamSide === "home" ? match.homeNation.name : match.awayNation.name;
    const score = teamSide === "home" ? liveScore.away : liveScore.home;

    if (score > 0) {
      if (
        !confirm(
          `${nation} has conceded ${score} goal(s). Award clean sheet anyway?`,
        )
      )
        return;
    }

    awardCleanSheet(teamSide);
  };

  const renderEventRow = (event: MatchEvent) => {
    const isHomeEvent = event.player.nation.name === match.homeNation.name;
    const minuteLabel = APPEARANCE_TYPES.includes(event.eventType)
      ? `${event.minute}min`
      : `${event.minute}'`;

    return (
      <div
        key={event.id}
        className={`flex items-start gap-2 ${isHomeEvent ? "flex-row" : "flex-row-reverse"}`}
      >
        {/* Event Card */}
        <div
          className={`flex-1 flex ${isHomeEvent ? "justify-start" : "justify-end"}`}
        >
          <div
            className={`max-w-[85%] p-2 rounded-lg border text-xs ${EVENT_COLORS[event.eventType]}`}
          >
            <div
              className={`flex items-center gap-2 ${isHomeEvent ? "flex-row" : "flex-row-reverse"}`}
            >
              <span className="text-base">{EVENT_ICONS[event.eventType]}</span>
              <div className={isHomeEvent ? "text-left" : "text-right"}>
                <p className="font-semibold">{event.player.name}</p>
                <p className="opacity-70">
                  {event.player.position}
                  {event.player.jerseyNumber &&
                    ` · #${event.player.jerseyNumber}`}
                </p>
              </div>
              <Badge
                variant="outline"
                className="text-xs border-current shrink-0"
              >
                {event.eventType.replace(/_/g, " ")}
              </Badge>
              <span
                className={`font-bold shrink-0 ${event.points < 0 ? "text-red-600" : "text-green-700"}`}
              >
                {event.points > 0 ? "+" : ""}
                {event.points}pt
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteEvent(event)}
                disabled={isDeleting}
                className="h-5 w-5 p-0 opacity-50 hover:opacity-100 shrink-0"
              >
                <Trash2 className="h-3 w-3 text-red-600" />
              </Button>
            </div>
          </div>
        </div>

        {/* Center minute marker */}
        <div className="flex flex-col items-center shrink-0 w-14">
          <div className="flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5 text-xs font-bold text-gray-600">
            <Clock className="h-2.5 w-2.5" />
            {minuteLabel}
          </div>
        </div>

        {/* Spacer for opposite side */}
        <div className="flex-1" />
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
      {/* Scoreboard */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <Badge className="bg-white/20 text-white border-white/30 animate-pulse">
            🔴 LIVE
          </Badge>
          <span className="text-green-100 text-sm">{match.stage}</span>
        </div>

        <div className="flex items-center justify-between">
          {/* Home Nation */}
          <div className="flex flex-col items-center gap-2 flex-1">
            {match.homeNation.flagUrl && (
              <img
                src={match.homeNation.flagUrl}
                alt={match.homeNation.name}
                className="w-14 h-9 object-cover rounded border-2 border-white/30"
              />
            )}
            <p className="font-bold text-lg text-center">
              {match.homeNation.name}
            </p>
          </div>

          {/* Score — with inline edit override */}
          <div className="flex flex-col items-center gap-2 px-6">
            {editingScore ? (
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={scoreInput.home}
                    onChange={(e) =>
                      setScoreInput((s) => ({ ...s, home: Math.max(0, Number(e.target.value)) }))
                    }
                    className="w-14 text-center text-3xl font-black rounded-lg text-gray-900 border-2 border-white p-1"
                  />
                  <span className="text-3xl font-black text-white">—</span>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={scoreInput.away}
                    onChange={(e) =>
                      setScoreInput((s) => ({ ...s, away: Math.max(0, Number(e.target.value)) }))
                    }
                    className="w-14 text-center text-3xl font-black rounded-lg text-gray-900 border-2 border-white p-1"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-white text-green-700 hover:bg-green-50 text-xs h-7 font-bold"
                    onClick={handleSaveScore}
                    disabled={isSavingScore}
                  >
                    {isSavingScore ? "Saving…" : "✅ Save Score"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white/70 hover:text-white border border-white/30 text-xs h-7"
                    onClick={() => setEditingScore(false)}
                    disabled={isSavingScore}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="text-5xl font-black tracking-wider">
                  {liveScore.home} — {liveScore.away}
                </div>
                <button
                  type="button"
                  onClick={handleOpenScoreEdit}
                  className="flex items-center gap-1 bg-white/20 hover:bg-white/35 border border-white/40 text-white text-xs font-medium px-2 py-1 rounded-full transition-colors"
                  title="Override score manually"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
              </div>
            )}
            <p className="text-green-200 text-sm mt-1">{match.venue || "—"}</p>
            {/* Timer Display */}
            <div
              className={`text-2xl font-bold font-mono ${
                timer.isExtraTime
                  ? "text-red-300"
                  : timer.isFullTime
                    ? "text-yellow-300"
                    : "text-white"
              }`}
            >
              {timer.isExtraTime ? `ET ${timer.formatted}` : timer.formatted}
            </div>

            {/* Timer Controls */}
            <div className="flex gap-2 flex-wrap justify-center">
              {/* Reset Timer Button - always available */}
              <Button
                size="sm"
                variant="ghost"
                className="text-red-300 border-red-300 border text-xs h-7"
                onClick={() => {
                  if (confirm("Reset timer to 0:00? This cannot be undone.")) {
                    timer.start(0);
                    toast.success("Timer reset to 0:00");
                  }
                }}
                title="Reset timer to 0:00"
              >
                🔄 Reset
              </Button>

              {/* Running first half — show both Pause and Half Time */}
              {timer.state === "running" && timer.isFirstHalf && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white border-white/30 border text-xs h-7"
                    onClick={timer.pause}
                  >
                    <Pause className="h-3 w-3 mr-1" /> Pause
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-yellow-300 border-yellow-300 border text-xs h-7"
                    onClick={timer.halfTime}
                  >
                    🔔 Half Time
                  </Button>
                </>
              )}

              {/* Running second half — show Pause only */}
              {timer.state === "running" &&
                !timer.isFirstHalf &&
                !timer.isFullTime && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white border-white/30 border text-xs h-7"
                    onClick={timer.pause}
                  >
                    <Pause className="h-3 w-3 mr-1" /> Pause
                  </Button>
                )}

              {/* Paused in first half — Resume or Start 2nd Half */}
              {timer.state === "paused" && timer.isFirstHalf && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white border-white/30 border text-xs h-7"
                    onClick={timer.resume}
                  >
                    <Play className="h-3 w-3 mr-1" /> Resume
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-green-300 border-green-300 border text-xs h-7"
                    onClick={timer.startSecondHalf}
                  >
                    <Play className="h-3 w-3 mr-1" /> Start 2nd Half
                  </Button>
                </>
              )}

              {/* Paused in second half — Resume only */}
              {timer.state === "paused" &&
                !timer.isFirstHalf &&
                !timer.isFullTime && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white border-white/30 border text-xs h-7"
                    onClick={timer.resume}
                  >
                    <Play className="h-3 w-3 mr-1" /> Resume
                  </Button>
                )}

              {/* Full time indicator */}
              {timer.isFullTime && (
                <div className="flex flex-col items-center gap-2">
                  <span className="text-yellow-300 text-xs font-bold border border-yellow-300 rounded px-2 py-1">
                    ⏱ Full Time
                  </span>
                  {!hasAppearances ? (
                    <span className="text-red-300 text-xs font-bold border border-red-300 rounded px-2 py-1 animate-pulse">
                      ⚠️ Log appearances &amp; award 60+ bonus first!
                    </span>
                  ) : (
                    <span className="text-green-300 text-xs border border-green-300 rounded px-2 py-1">
                      ✅ Appearances logged — use "Mark Complete" below
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Away Nation */}
          <div className="flex flex-col items-center gap-2 flex-1">
            {match.awayNation.flagUrl && (
              <img
                src={match.awayNation.flagUrl}
                alt={match.awayNation.name}
                className="w-14 h-9 object-cover rounded border-2 border-white/30"
              />
            )}
            <p className="font-bold text-lg text-center">
              {match.awayNation.name}
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-600 font-medium">
            {events?.length || 0} total events
          </p>
          <Button
            onClick={handleCompleteMatch}
            variant="outline"
            size="sm"
            className={`text-xs font-semibold ${
              hasAppearances && timer.isFullTime
                ? "border-blue-600 text-blue-700 hover:bg-blue-50"
                : "border-gray-400 text-gray-500 hover:bg-gray-100"
            }`}
            disabled={isCompleting}
            title="Mark this match as completed"
          >
            {isCompleting ? "Completing…" : "✅ Mark Complete"}
          </Button>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {/* Log Starters — prominent when no appearances yet */}
          <Button
            onClick={() => handleShowBulk(true)}
            variant="outline"
            className={`text-xs ${
              !hasAppearances
                ? "border-green-600 text-green-700 hover:bg-green-50 font-semibold"
                : "border-gray-300 text-gray-500 hover:bg-gray-50"
            }`}
            disabled={isLogging}
            title="Log the starting 11 at kick-off — each gets +1pt immediately"
          >
            <Users className="h-3.5 w-3.5 mr-1.5" />
            {!hasAppearances ? "Log Starters" : "Starters"}
          </Button>

          {/* Log Appearances (full-time: 60+ bonus) */}
          <Button
            onClick={() => handleShowBulk(false)}
            variant="outline"
            className={`text-xs ${
              timer.isFullTime
                ? "border-violet-600 text-violet-700 hover:bg-violet-50 font-semibold"
                : "border-blue-600 text-blue-600 hover:bg-blue-50"
            }`}
            disabled={isLogging}
            title="At full time — award +1pt bonus to players who played 60+ min"
          >
            <Users className="h-3.5 w-3.5 mr-1.5" />
            {timer.isFullTime ? "60+ Min Bonus" : "Appearances"}
          </Button>
          <Button
            onClick={handleShowForm}
            className="bg-green-600 hover:bg-green-700 text-white"
            disabled={isLogging}
          >
            <Plus className="h-4 w-4 mr-2" />
            Log Event
          </Button>
          <Button
            onClick={() => handleCleanSheet("home")}
            variant="outline"
            className="border-teal-600 text-teal-600 hover:bg-teal-50 text-xs"
            disabled={isLogging}
            title={`Award clean sheet to ${match.homeNation.name}`}
          >
            🧤 {match.homeNation.code} CS
          </Button>
          <Button
            onClick={() => handleCleanSheet("away")}
            variant="outline"
            className="border-teal-600 text-teal-600 hover:bg-teal-50 text-xs"
            disabled={isLogging}
            title={`Award clean sheet to ${match.awayNation.name}`}
          >
            🧤 {match.awayNation.code} CS
          </Button>
        </div>
      </div>

      {/* Event Log Form */}
      {showForm && (
        <div className="px-6 py-4 border-b bg-blue-50">
          <EventLogForm
            match={match}
            onSubmit={handleLogEvent}
            onCancel={() => setShowForm(false)}
            isLogging={isLogging}
            defaultMinute={currentMinute}
          />
        </div>
      )}

      {/* Bulk Appearance Form */}
      {showBulkAppearance && (
        <div className={`px-6 py-4 border-b ${startersMode ? "bg-green-50" : "bg-indigo-50"}`}>
          <BulkAppearanceForm
            match={match}
            onClose={() => setShowBulkAppearance(false)}
            startersMode={startersMode}
            currentMatchMinute={currentMinute}
          />
        </div>
      )}

      <div className="p-6 space-y-4">
        {/* ── Match Events Card ── */}
        <div className="border rounded-xl overflow-hidden">
          <button
            onClick={() => setEventsExpanded(!eventsExpanded)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition"
          >
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-800">
                ⚽ Match Events
              </span>
              <Badge variant="outline" className="text-xs">
                {matchEvents.length}
              </Badge>
            </div>
            {eventsExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </button>

          {eventsExpanded && (
            <div className="p-4">
              {/* Nation headers */}
              <div className="flex items-center justify-between mb-3 px-16">
                <div className="flex items-center gap-1">
                  {match.homeNation.flagUrl && (
                    <img
                      src={match.homeNation.flagUrl}
                      className="w-5 h-3 object-cover rounded"
                      alt=""
                    />
                  )}
                  <span className="text-xs font-bold text-gray-600">
                    {match.homeNation.name}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-bold text-gray-600">
                    {match.awayNation.name}
                  </span>
                  {match.awayNation.flagUrl && (
                    <img
                      src={match.awayNation.flagUrl}
                      className="w-5 h-3 object-cover rounded"
                      alt=""
                    />
                  )}
                </div>
              </div>

              {eventsLoading ? (
                <div className="flex justify-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600" />
                </div>
              ) : matchEvents.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-6">
                  No match events yet
                </p>
              ) : (
                <div className="space-y-2">
                  {matchEvents.map((event) => renderEventRow(event))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Appearances Card ── */}
        <div className="border rounded-xl overflow-hidden">
          <button
            onClick={() => setAppearancesExpanded(!appearancesExpanded)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition"
          >
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-800">
                👟 Appearances
              </span>
              <Badge variant="outline" className="text-xs">
                {appearanceEvents.length}
              </Badge>
            </div>
            {appearancesExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </button>

          {appearancesExpanded && (
            <div className="p-4">
              {/* Nation headers */}
              <div className="flex items-center justify-between mb-3 px-16">
                <div className="flex items-center gap-1">
                  {match.homeNation.flagUrl && (
                    <img
                      src={match.homeNation.flagUrl}
                      className="w-5 h-3 object-cover rounded"
                      alt=""
                    />
                  )}
                  <span className="text-xs font-bold text-gray-600">
                    {match.homeNation.name}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-bold text-gray-600">
                    {match.awayNation.name}
                  </span>
                  {match.awayNation.flagUrl && (
                    <img
                      src={match.awayNation.flagUrl}
                      className="w-5 h-3 object-cover rounded"
                      alt=""
                    />
                  )}
                </div>
              </div>

              {appearanceEvents.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-6">
                  No appearances logged yet
                </p>
              ) : (
                <div className="space-y-2">
                  {appearanceEvents.map((event) => renderEventRow(event))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
